import axios from 'axios';

// Deployment-safe base URL: explicit env var > dev default (:3010) > the page's
// own origin in production (same-origin / reverse-proxy). A hardcoded localhost
// fallback would break this service in any deployed build.
const BASE_URL = import.meta.env.VITE_APP_API_URL || (import.meta.env.DEV ? 'http://localhost:3010' : window.location.origin);
const API_BASE = `${BASE_URL}/api/v1`;

// Create axios instance with auth header
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Create new feedback
 * @param {Object} feedback - Feedback data
 * @param {number} userId - Current user ID
 */
export const createFeedback = async (feedback, userId) => {
  const response = await axios.post(`${API_BASE}/feedback?user_id=${userId}`, feedback, { headers: getAuthHeaders() });
  return response.data;
};

/**
 * Get feedback list with filters
 * @param {Object} params - Query parameters
 */
export const getFeedbackList = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.pagePath) queryParams.append('page_path', params.pagePath);
  if (params.status) queryParams.append('status', params.status);
  if (params.feedbackType) queryParams.append('feedback_type', params.feedbackType);
  if (params.page) queryParams.append('page', params.page);
  if (params.pageSize) queryParams.append('page_size', params.pageSize);

  const response = await axios.get(`${API_BASE}/feedback?${queryParams.toString()}`, { headers: getAuthHeaders() });
  return response.data;
};

/**
 * Get single feedback by ID
 * @param {number} feedbackId - Feedback ID
 */
export const getFeedback = async (feedbackId) => {
  const response = await axios.get(`${API_BASE}/feedback/${feedbackId}`, { headers: getAuthHeaders() });
  return response.data;
};

/**
 * Update feedback
 * @param {number} feedbackId - Feedback ID
 * @param {Object} update - Update data
 * @param {number} userId - Current user ID
 */
export const updateFeedback = async (feedbackId, update, userId) => {
  const response = await axios.patch(`${API_BASE}/feedback/${feedbackId}?user_id=${userId}`, update, { headers: getAuthHeaders() });
  return response.data;
};

/**
 * Add comment to feedback
 * @param {number} feedbackId - Feedback ID
 * @param {Object} comment - Comment data
 * @param {number} userId - Current user ID
 */
export const addComment = async (feedbackId, comment, userId) => {
  const response = await axios.post(`${API_BASE}/feedback/${feedbackId}/comments?user_id=${userId}`, comment, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Delete feedback
 * @param {number} feedbackId - Feedback ID
 * @param {number} userId - Current user ID
 */
export const deleteFeedback = async (feedbackId, userId) => {
  await axios.delete(`${API_BASE}/feedback/${feedbackId}?user_id=${userId}`, { headers: getAuthHeaders() });
};

// Feedback types
export const FEEDBACK_TYPES = {
  bug: { label: 'Bug', color: 'error' },
  suggestion: { label: 'Suggestion', color: 'info' },
  improvement: { label: 'Improvement', color: 'success' },
  other: { label: 'Other', color: 'default' }
};

// Priority levels
export const PRIORITY_LEVELS = {
  low: { label: 'Low', color: 'success' },
  medium: { label: 'Medium', color: 'warning' },
  high: { label: 'High', color: 'error' },
  critical: { label: 'Critical', color: 'error' }
};

// Status options
export const STATUS_OPTIONS = {
  open: { label: 'Open', color: 'info' },
  in_progress: { label: 'In Progress', color: 'warning' },
  resolved: { label: 'Resolved', color: 'success' },
  closed: { label: 'Closed', color: 'default' }
};
